'use client';

import { useState, FormEvent } from 'react';
import { Button, Input, Textarea } from '@/components/ui';
import { companyInfo } from '@/lib/constants';
import type { ContactFormData } from '@/types';

export function validateContactForm(
  data: ContactFormData
): Partial<ContactFormData> {
  const errors: Partial<ContactFormData> = {};

  if (!data.name.trim()) {
    errors.name = 'Name is required';
  }

  if (!data.email.trim()) {
    errors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = 'Please enter a valid email';
  }

  if (!data.message.trim()) {
    errors.message = 'Tell me a little about what you’re building';
  }

  return errors;
}

export async function submitContactForm(data: ContactFormData): Promise<void> {
  const formspreeId = process.env.NEXT_PUBLIC_FORMSPREE_ID;
  if (!formspreeId) {
    throw new Error('Missing NEXT_PUBLIC_FORMSPREE_ID env var');
  }

  const response = await fetch(`https://formspree.io/f/${formspreeId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(
      `Formspree submission failed with status ${response.status}`
    );
  }
}

export interface ContactFormProps {
  /** Prefix for field ids, since this form can render more than once on a page. */
  idPrefix?: string;
}

export function ContactForm({ idPrefix = 'contact' }: ContactFormProps = {}) {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    message: '',
  });

  const [errors, setErrors] = useState<Partial<ContactFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const newErrors = validateContactForm(formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await submitContactForm(formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <Input
        id={`${idPrefix}-name`}
        label="Your name"
        type="text"
        value={formData.name}
        onChange={(e) => handleChange('name', e.target.value)}
        error={errors.name}
        required
        disabled={isSubmitting}
      />

      <Textarea
        id={`${idPrefix}-message`}
        label="What are you building?"
        rows={4}
        value={formData.message}
        onChange={(e) => handleChange('message', e.target.value)}
        error={errors.message}
        required
        disabled={isSubmitting}
        placeholder="A few lines is plenty to start."
      />

      <Input
        id={`${idPrefix}-email`}
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => handleChange('email', e.target.value)}
        error={errors.email}
        required
        disabled={isSubmitting}
      />

      {submitStatus === 'success' && (
        <div
          className="border-l-2 border-airway bg-wash px-4 py-3 text-[0.95rem] text-ink"
          role="alert"
        >
          Thanks, I&apos;ll get back to you soon.
        </div>
      )}

      {submitStatus === 'error' && (
        <div
          className="border-l-2 border-beacon bg-wash px-4 py-3 text-[0.95rem] text-ink"
          role="alert"
        >
          Something went wrong sending that. Try again, or email{' '}
          <a href={`mailto:${companyInfo.email}`} className="underline">
            {companyInfo.email}
          </a>{' '}
          directly.
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        className="w-full"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Sending…' : 'Send it over'}
      </Button>
    </form>
  );
}
